import { useInfiniteQuery, useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Get } from "../config/api/get";
import { Post } from "../config/api/post";
import { useSelector } from "react-redux";
import { CLASSROOM, TIMETABLE } from "../config/constants";
import swal from "../utils/swal";


export const useSearchParents = (params) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["search-parents", params],
    queryFn: async () => {
      try {
        const { data } = await Get(CLASSROOM.searchParents, token, params);
        
        return data.parents || []; // Default response structure
      } catch (error) {
        console.error("Error fetching parents:", error);
        throw error.data;
      }
    },
    enabled: !!token || !!params.keyword, // Fetch only if token exists
    keepPreviousData: true, // Smooth transitions
  });
};

export const useSearchTeachers = (params) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["search-teachers", params],
    queryFn: async () => {
      try {
        const { data } = await Get(CLASSROOM.searchTeachers, token, params);
        
        return data.teachers || []; // Default response structure
      } catch (error) {
        console.error("Error fetching teachers:", error);
        throw error.data;
      }
    },
    enabled: !!token || !!params.keyword, // Fetch only if token exists
    keepPreviousData: true, // Smooth transitions
  });
};

export const useTeacherPages = (keyword) => {
  const token = useSelector((state) => state.user.userToken);
  return useInfiniteQuery({
    queryKey: ["search-teachers-pages", keyword || ""],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const { data } = await Get(CLASSROOM.searchTeachers, token, {
        keyword: keyword || undefined,
        page: pageParam,
        limit: 20,
      });
      return {
        teachers: data?.teachers || [],
        page: Number(data?.page) || pageParam,
        totalPages: Number(data?.totalPages) || 1,
      };
    },
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    enabled: !!token,
  });
};

const CLASSES_QUERY_KEY = ["get-classren"];

const invalidateClasses = (queryClient, classId) => {
  queryClient.invalidateQueries({ queryKey: CLASSES_QUERY_KEY });
  if (classId) queryClient.invalidateQueries({ queryKey: ["class", classId] });
};

export const useGetAllClasses = (params = {}) => {
  const token = useSelector((state) => state.user.userToken);

  return useQuery({
    queryKey: [...CLASSES_QUERY_KEY, params],
    queryFn: async ({ signal }) => {
      try {
        const { data } = await Get(CLASSROOM.getAllClassrooms, token, params, { signal });
        return data || { docs: [], totalPages: 1, totalDocs: 0 };
      } catch (error) {
        console.error("Error fetching classes:", error);
        throw error.data;
      }
    },
    enabled: !!token,
    placeholderData: keepPreviousData,
  });
};


export const useUpdateClassStatus = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ classId, status }) => {
      try {
        const response = await Post(CLASSROOM.updateClass + classId, { status }, token);
        if (response?.data?.status) {
          return response.data; 
        } else {
          throw new Error(response?.data?.message || response?.response?.data?.message || "Failed to create class");
        }

      } catch (error) {
        console.error("Error toggling status:", error);
        throw error.data;
      }
    },
    onSuccess: (_data, variables) => {
      invalidateClasses(queryClient, variables?.classId);
    },
  });
};


export const useAddClass = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ data }) => {
      try {
        const response = await Post(CLASSROOM.addClassroom, data, token, null);

       if (response?.data?.status) {
          return response.data; // Success case
        } else {
          throw new Error(response?.data?.message || response?.response?.data?.message || "Failed to create class");
        }
      } catch (error) {
        console.error("Error adding class:", error);
         throw error.data;
      }
    },
    onSuccess: () => {
      invalidateClasses(queryClient);
    }
  });
};


export const useGetClassById = (classId) => {
  const token = useSelector((state) => state.user.userToken);

  return useQuery({
    queryKey: ["class", classId],
    queryFn: async () => {
      try {
        const response = await Get(CLASSROOM.getClassroomById + classId, token);
        return response.data.classroom || {}; // Default response structure
      } catch (error) {
        console.error("Error fetching class:", error);
        throw error.data;
      }
    },
    enabled: !!token,
  });
}

export const useGetClassrooms = (values) => {
  const token = useSelector((state) => state.user.userToken);

  return useQuery({
    queryKey: "classrooms",
    queryFn: async () => {
      const response = await Get(`${CLASSROOM.getAllClassrooms}`, token);
      return response.data.docs;
    },
  })
};

export const useUpdateClass = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ classId, data }) => {
      console.log("dataLLLLL", data)
      try {
        const response = await Post(CLASSROOM.updateClass + classId, data, token, null);
        // console.log("dataLLLLL", response)
        if (response?.data?.status) {
          return response.data; // Success case
        } else {
          throw new Error(response?.data?.message || response?.response?.data?.message || "Failed to create class");
        }
      } catch (error) {
        console.error("Error updating class:", error);
        throw error.data;
      }
    },
    onSuccess: (_data, variables) => {
      invalidateClasses(queryClient, variables?.classId);
    },
  })
};

export const useDeleteClass = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async (classId) => {
      const response = await Get(CLASSROOM.deleteClass + classId, token);
      const body =
        typeof response?.status === "boolean"
          ? response
          : response?.response?.data || response?.data;
      if (!body?.status) {
        throw new Error(body?.message || "Failed to delete class");
      }
      return body;
    },
    onSuccess: () => {
      invalidateClasses(queryClient);
    },
  });
};


export const useMarkAttendance = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ classId, data }) => {
      try {
        const response = await Post(CLASSROOM.markAttendance + classId, data, token);
        return response.data;
      } catch (error) {
        console.error("Error marking attendance:", error);
        throw error.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["class-attendance"]);
    },
  });
}

export const useUpdateAttendance = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ attendanceId, data }) => {
      try {
        const response = await Post(CLASSROOM.updateAttendance + attendanceId, data, token);
        console.log(">>>>>>", response)
        return response.data;
      } catch (error) {
        swal("Error", error.data.message, "error");
        throw error.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["class"]);
      queryClient.invalidateQueries(["get-classren"]);
      queryClient.invalidateQueries(["class-attendance"]);
    },
  });
}

export const useDeleteAttendance = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async (attendanceId) => {
      try {
        const response = await Post(CLASSROOM.deleteAttendance + attendanceId,{}, token);
        return response.data;
      } catch (error) {
        console.error("Error deleting class:", error);
        throw error.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["class-attendance"]);
    },
  });
}

export const useGetClassTimetables = (classroomId) => {
  const token = useSelector((state) => state.user.userToken);
  const id = classroomId ? String(classroomId) : "";

  return useQuery({
    queryKey: ["class-timetable", "v2", id],
    queryFn: async () => {
      const response = await Get(TIMETABLE.getAllClassTimetables + id, token);
      if (!response?.status) {
        throw new Error(response?.message || "Failed to load timetable");
      }
      return groupTimetableByDay(response.data);
    },
    enabled: !!token && !!id,
    staleTime: 0,
  });
};

function groupTimetableByDay(payload = {}) {
  const grouped = { MON: [], TUE: [], WED: [], THU: [], FRI: [] };
  const slots = Array.isArray(payload)
    ? payload
    : Array.isArray(payload.slots)
      ? payload.slots
      : null;

  if (slots) {
    slots.forEach((slot) => {
      const day = String(slot.day || "").toUpperCase();
      if (grouped[day]) grouped[day].push(slot);
    });
    return grouped;
  }

  const source = payload.byDay && typeof payload.byDay === "object" ? payload.byDay : payload;
  Object.keys(grouped).forEach((day) => {
    if (Array.isArray(source[day])) grouped[day] = source[day];
  });
  return grouped;
}